/**
 * Unique-id generation that works on Hermes.
 *
 * `crypto.randomUUID()` is a browser global. Hermes has no `crypto` at all, so
 * calling it on the native app threw "Property 'crypto' doesn't exist" the moment
 * a job was submitted.
 *
 * No polyfill dependency: the only consumer is the submit idempotency key, which
 * is a de-duplication token, not a security token. The server scopes it per
 * caller and per Firebase uid (`FindJobByIdempotency(key, callerID, uid)`), so a
 * collision is only possible between two keys from the same account. The
 * monotonic counter makes that impossible within one JS runtime, and the
 * timestamp plus randomness covers separate runtimes.
 *
 * If a genuinely random id is ever needed for a security purpose, add
 * react-native-get-random-values rather than reaching for this.
 */

let counter = 0;

/** Per-runtime prefix, so two devices on the same account cannot collide. */
const runtimePrefix = Math.random().toString(36).slice(2, 10);

function randomBlock(): string {
  const g = globalThis as { crypto?: { getRandomValues?: (a: Uint32Array) => Uint32Array } };
  if (g.crypto?.getRandomValues) {
    const buf = new Uint32Array(2);
    g.crypto.getRandomValues(buf);
    return `${buf[0].toString(36)}${buf[1].toString(36)}`;
  }
  return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** RFC 4122 v4 when the platform can, otherwise a collision-resistant token. */
export function uniqueId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  counter += 1;
  return `${runtimePrefix}-${Date.now().toString(36)}-${counter.toString(36)}-${randomBlock()}`;
}
